<template>
  <main class="main main--has-floating-buttons">
    <portal to="headerTitle">
      {{ $t('admin.pageRatePlans.title') }}
    </portal>
    <template v-if="ratePlans !== null">
      <template v-if="sortingMode">
        <card :title="$t('admin.pageRatePlans.card.sortAllRatePlans')" cols="12">
          <hm-card-inner :full-size="true">
            <hm-col cols="12">
              <draggable v-model="allRatePlans" group="ratePlans">
                <template v-for="ratePlan in allRatePlans" v-if="!ratePlan.deleted">
                  <div class="card__inner--line__wrapper">
                    <hm-card-inner-line :title="getName(ratePlan, true)" :clickable="true" :key="ratePlan.id" @click="routeToRatePlan(ratePlan)" />
                  </div>
                </template>
              </draggable>
            </hm-col>
          </hm-card-inner>
        </card>
      </template>
      <template v-else>
        <card :title="$t('admin.pageRatePlans.card.ratePlans')" cols="12">
          <hm-card-inner :full-size="true">
            <hm-col cols="12">
              <template v-for="ratePlan in ratePlans" v-if="ratePlan.type === 'DayRate' && !ratePlan.deleted">
                <div class="card__inner--line__wrapper">
                  <hm-card-inner-line :title="getName(ratePlan)" :clickable="true" :key="ratePlan.id" @click="routeToRatePlan(ratePlan)" />
                  <template v-for="ratePlan1 in ratePlan.childRatePlans" v-if="ratePlan1.type === 'DayRate' && !ratePlan1.deleted">
                    <hm-card-inner-line :title="`└ ${getName(ratePlan1)}`" :clickable="true" :key="ratePlan1.id" @click="routeToRatePlan(ratePlan1)" />
                    <template v-for="ratePlan2 in ratePlan1.childRatePlans" v-if="ratePlan2.type === 'DayRate' && !ratePlan2.deleted">
                      <hm-card-inner-line :title="`&nbsp;&nbsp;&nbsp;└ ${getName(ratePlan2)}`" :clickable="true" :key="ratePlan2.id" @click="routeToRatePlan(ratePlan2)" />
                      <template v-for="ratePlan3 in ratePlan2.childRatePlans" v-if="ratePlan3.type === 'DayRate' && !ratePlan3.deleted">
                        <hm-card-inner-line :title="`&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;└ ${getName(ratePlan3)}`" :clickable="true" :key="ratePlan3.id" @click="routeToRatePlan(ratePlan3)" />
                      </template>
                    </template>
                  </template>
                </div>
              </template>
            </hm-col>
          </hm-card-inner>
        </card>

        <card v-if="hasLicense('Packages')" :title="$t('admin.pageRatePlans.card.packages')" cols="12">
          <hm-card-inner :full-size="true">
            <hm-col cols="12">
              <template v-for="ratePlan in ratePlans" v-if="ratePlan.type === 'Package' && !ratePlan.deleted">
                <div class="card__inner--line__wrapper">
                  <hm-card-inner-line :title="getName(ratePlan)" :clickable="true" :key="ratePlan.id" @click="routeToRatePlan(ratePlan)" />
                  <template v-for="ratePlan1 in ratePlan.childRatePlans" v-if="ratePlan1.type === 'Package' && !ratePlan1.deleted">
                    <hm-card-inner-line :title="`└ ${getName(ratePlan1)}`" :clickable="true" :key="ratePlan1.id" @click="routeToRatePlan(ratePlan1)" />
                    <template v-for="ratePlan2 in ratePlan1.childRatePlans" v-if="ratePlan2.type === 'Package' && !ratePlan2.deleted">
                      <hm-card-inner-line :title="`&nbsp;&nbsp;&nbsp;└ ${getName(ratePlan2)}`" :clickable="true" :key="ratePlan2.id" @click="routeToRatePlan(ratePlan2)" />
                      <template v-for="ratePlan3 in ratePlan2.childRatePlans" v-if="ratePlan3.type === 'Package' && !ratePlan3.deleted">
                        <hm-card-inner-line :title="`&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;└ ${getName(ratePlan3)}`" :clickable="true" :key="ratePlan3.id" @click="routeToRatePlan(ratePlan3)" />
                      </template>
                    </template>
                  </template>
                </div>
              </template>
            </hm-col>
          </hm-card-inner>
        </card>

        <card v-if="!isEmpty(deletedRatePlans)" :title="$t('admin.pageRatePlans.card.deletedRatePlans')" cols="12" card-type="collapsible">
          <hm-card-inner :full-size="true">
            <hm-card-inner-line v-for="ratePlan in deletedRatePlans"
                                :title="getName(ratePlan)"
                                :clickable="true"
                                :key="ratePlan.id"
                                @click="routeToRatePlan(ratePlan)" />
          </hm-card-inner>
        </card>
      </template>

    </template>

    <hm-floating-buttons id="actions" ref="floatingButtons" >
      <template v-if="sortingMode">
        <hm-floating-button :label="$t('admin.pageRatePlans.actions.cancelSort')"
                            :throttle="0"
                            icon="delete"
                            color="gray"
                            @click="sortingMode = false" />
      </template>
      <template v-else>
        <hm-floating-button :throttle="0"
                            :label="$t('admin.pageRatePlans.actions.sort')"
                            icon="fill-sort"
                            color="orange"
                            @click="sortingMode = true" />
        <hm-floating-button :label="$t('admin.pageRatePlans.actions.more')" icon="plus" color="blue">
          <hm-floating-child-button :label="$t('admin.pageRatePlans.actions.addRatePlan')" icon="plus" color="blue" @click="addRatePlan('DayRate', 'Seasonal')" />
          <hm-floating-child-button v-if="hasLicense('DailyRateEntry')"
                                    :label="$t('admin.pageRatePlans.actions.addRatePlanDaily')"
                                    icon="plus"
                                    color="blue"
                                    @click="addRatePlan('DayRate', 'Daily')" />
          <hm-floating-child-button v-if="hasLicense('Packages')"
                                    :label="$t('admin.pageRatePlans.actions.addPackage')"
                                    icon="plus"
                                    color="blue"
                                    @click="addRatePlan('Package', 'Seasonal')" />
          <hm-floating-child-button v-if="hasLicense('Packages') && hasLicense('DailyRateEntry')"
                                    :label="$t('admin.pageRatePlans.actions.addPackageDaily')"
                                    icon="plus"
                                    color="blue"
                                    @click="addRatePlan('Package', 'Daily')" />
        </hm-floating-button>
      </template>
    </hm-floating-buttons>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import vue2Dropzone from 'vue2-dropzone';
import draggable from 'vuedraggable';
import { cloneDeep, flattenDeep, groupBy, isEmpty, isNil, keyBy, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';

export default {
  name: 'PageRatePlans',

  components: {
    draggable,
  },

  autosave: [
    {
      name: 'ratePlans',
      variable: 'sortedRatePlanIds',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        const update = {
          ratePlanIds: this.sortedRatePlanIds,
        };

        this.axios.put(this.serviceUrls.ratePlan.sort(this, this.$route.params.ratePlanId), update)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
            this.$serviceBus.$emit('ratePlan.list', this.ratePlans);
          })
          .catch((error) => {
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  mixins: [HotelmeisterMixin, SaveWatchMixin],

  data() {
    return {
      ratePlans: null,
      allRatePlans: null,
      sortingMode: false,
    };
  },

  computed: {
    deletedRatePlans: function () {
      return isNil(this.ratePlans) ? null : this.ratePlans.filter(it => it.deleted === true);
    },
    sortedRatePlanIds: function () {
      return isNil(this.allRatePlans) ? null : this.allRatePlans.filter(it => !it.deleted).map(it => it.id);
    },
  },

  watch: {
  },

  created: function () {
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    flattenRatePlanIds(ratePlan = null) {
      const ret = [];
      if (ratePlan === null) {
        this.ratePlans.forEach(itRp => ret.push(this.flattenRatePlanIds(itRp)));
      } else {
        ret.push(ratePlan.id);
        if (!isEmpty(ratePlan.childRatePlans)) {
          ratePlan.childRatePlans.forEach(itRp => ret.push(this.flattenRatePlanIds(itRp)));
        }
      }

      return flattenDeep(ret);
    },
    loadData() {
      this.axios.get(this.serviceUrls.ratePlan.list(this))
        .then((response) => {
          let ratePlans = response.data.data;
          this.allRatePlans = response.data.data;

          ratePlans = sortBy(ratePlans, (it) => {
            if (it.deleted === true) return 3;
            else if (it.type === 'DayRate') return 1;
            else return 2;
          }, ['sort', `name.${this.hotelSettings.details.primaryLanguage}`]);

          const parentRatePlans = ratePlans.filter(it => !it.deleted && it.ratePlanMode !== 'Derived');
          const deletedRatePlans = ratePlans.filter(it => it.deleted);
          const targetRatePlans = parentRatePlans.concat(deletedRatePlans);

          const childRatePlans = ratePlans.filter(it => !it.deleted && it.ratePlanMode === 'Derived');
          const ratePlanIdMap = keyBy(targetRatePlans, 'id');

          for (let i = 0; !isEmpty(childRatePlans) && i < 5; i++) {
            childRatePlans.forEach((item, index, object) => {
              if (!isNil(item.parentRatePlanId) && !isNil(ratePlanIdMap[item.parentRatePlanId])) {
                const parentRatePlan = ratePlanIdMap[item.parentRatePlanId];
                if (isNil(parentRatePlan.childRatePlans)) {
                  parentRatePlan.childRatePlans = [];
                }
                parentRatePlan.childRatePlans.push(item);
                ratePlanIdMap[item.id] = item;
                object.splice(index, 1);
              }
            });
          }

          ratePlans.forEach((ratePlan) => {
            if (!isNil(ratePlan) && !isNil(ratePlan.childRatePlans)) {
              ratePlan.childRatePlans = sortBy(ratePlan.childRatePlans, ['sort', obj => obj.name[this.hotelSettings.details.primaryLanguage]]);
            }
          });

          this.ratePlans = targetRatePlans;

          this.$serviceBus.$emit('ratePlan.list', this.ratePlans);
        });
    },
    addRatePlan(type = 'DayRate', rateEntryMode = 'Seasonal') {
      this.axios.post(this.serviceUrls.ratePlan.list(this), {
        name: {
          [this.hotelSettings.details.primaryLanguage]: `${type === 'DayRate' ? this.$t('admin.pageRatePlans.newRatePlanName') : this.$t('admin.pageRatePlans.newPackageName')}`,
        },
        description: {
          [this.hotelSettings.details.primaryLanguage]: '',
        },
        type: isEmpty(type) ? 'DayRate' : type,
        rateEntryMode: isEmpty(rateEntryMode) ? 'Seasonal' : rateEntryMode,
        ratePlanMode: 'Regular',
        publicVisible: false,
      })
        .then((response) => {
          this.loadData();
          this.$serviceBus.$emit('hotelSettings.update');
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        });
    },
    getName(ratePlan, sortingMode = false) {
      const name = isNil(ratePlan) ? null : ratePlan.name;
      if (isNil(name)) return '-';
      let ret = null;

      ret = name[this.$shortLocale] || name[this.hotelSettings.details.primaryLanguage];

      if (!isNil(ratePlan.internalName)) {
        const internalName = ratePlan.internalName[this.$shortLocale] || ratePlan.internalName[this.hotelSettings.details.primaryLanguage];
        if (!isEmpty(internalName)) {
          ret += ` [${internalName}]`;
        }
      }

      let details = '';

      if (ratePlan.deleted === false) {
        if (sortingMode) {
          details += `, ${this.$t(`RatePlanType.${ratePlan.type}`)}`;
        }

        if (ratePlan.publicVisible === true) {
          details += `, ${this.$t('admin.pageRatePlans.description.publicVisible')}`;
        }

        if (!isEmpty(ratePlan.includedMealPlan)) {
          details += `, ${this.$t('admin.pageRatePlans.description.mealPlan')}: ${this.findHotelSettingById('mealPlans', ratePlan.includedMealPlan).name[this.$shortLocale]}`;
        }

        if (!isNil(ratePlan.webPrepayment) && ratePlan.webPrepayment !== 0) {
          details += `, ${this.$t('admin.pageRatePlans.description.prePayment')}: ${ratePlan.webPrepayment.toLocaleString(this.$i18n.locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 3,
          })} %`;
        }

        if (ratePlan.ratePlanMode === 'Derived' && !isEmpty(ratePlan.parentRatePlanId) && ratePlan.useSimpleRelativeRateAdjustment === true && !isNil(ratePlan.simpleRelativeRateAdjustmentAmount)) {
          details += `, ${this.$t('admin.pageRatePlans.description.simpleRelativeRateAdjustment')}: ${ratePlan.simpleRelativeRateAdjustmentAmount > 0 ? '+' : ''}${ratePlan.simpleRelativeRateAdjustmentAmount.toLocaleString(this.$i18n.locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 3,
          })} ${ratePlan.simpleRelativeRateAdjustmentPercentage ? '%' : this.hotelSettings.details.currency}`;
        }

        if (!isEmpty(ratePlan.validityDateRanges) && ratePlan.type === 'Package') {
          ratePlan.validityDateRanges.forEach((validityDateRange) => {
            details += `, ${this.$options.filters.toDate(validityDateRange.startDate)} - ${this.$options.filters.toDate(validityDateRange.endDate)}`;
          });
        }

        if (sortingMode && !isEmpty(ratePlan.parentRatePlanId)) {
          const parentRatePlan = this.findHotelSettingById('ratePlans', ratePlan.parentRatePlanId);
          if (!isNil(parentRatePlan)) {
            details += `, ↥ ${parentRatePlan.name[this.$shortLocale] || parentRatePlan.name[this.hotelSettings.details.primaryLanguage]}`;
          }
        }
      }

      if (!isNil(ret)) {
        ret = ret.trim();
      }

      if (isEmpty(ret)) {
        ret = '-';
      }

      ret = `${ret}<span class="color--gray">${details}</span>`;

      if (!isEmpty(ret)) return ret;

      return name[this.hotelSettings.details.primaryLanguage];
    },
    routeToRatePlan(ratePlan) {
      this.$router.push({
        name: 'ratePlan',
        params: {
          ratePlanId: ratePlan.id,
        },
      });
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageRatePlans.vue